import {
  filters as fabricFilters,
  Color,
  type T2DPipelineState,
  TWebGLUniformLocationMap,
} from "fabric";

type DuotoneOwnProps = {
  highlight: string;
  shadow: string;
  opacity: number;
};

class Duotone extends fabricFilters.BaseFilter<"Duotone", DuotoneOwnProps> {
  static type = "Duotone";

  declare highlight: DuotoneOwnProps["highlight"];
  declare shadow: DuotoneOwnProps["shadow"];
  declare opacity: DuotoneOwnProps["opacity"];

  static uniformLocations = ["uHighlight", "uShadow", "uOpacity"];

  getFragmentSource() {
    return `
       precision highp float;
       uniform sampler2D uTexture;
       uniform vec3 uHighlight;
       uniform vec3 uShadow;
       uniform float uOpacity;
       varying vec2 vTexCoord;

       void main() {
         vec4 color = texture2D(uTexture, vTexCoord);
         float brightness = dot(color.rgb, vec3(0.299, 0.587, 0.114));
         vec3 duotone = mix(uShadow, uHighlight, brightness);
         vec3 result = mix(color.rgb, duotone, uOpacity);
         gl_FragColor = vec4(result, color.a);
       }
     `;
  }

  constructor(options: DuotoneOwnProps) {
    super(options);

    this.highlight = options.highlight;
    this.shadow = options.shadow;
    this.opacity = options.opacity;
  }

  static async fromObject(object: any): Promise<Duotone> {
    return new this(object);
  }

  toObject(): { type: "Duotone" } & DuotoneOwnProps {
    return {
      type: this.type,
      highlight: this.highlight,
      shadow: this.shadow,
      opacity: this.opacity,
    };
  }

  applyTo2d({ imageData: { data } }: T2DPipelineState) {
    const len = data.length;
    const hightlight = Color.fromHex(this.highlight);
    const shadow = Color.fromHex(this.shadow);

    for (let i = 0; i < len; i += 4) {
      // Convert to grayscale first
      const brightness =
        (data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114) / 255;

      // Interpolate between the two colors based on brightness
      const r = Math.round(
        lerp(shadow.getSource()[0], hightlight.getSource()[0], brightness),
      );
      const g = Math.round(
        lerp(shadow.getSource()[1], hightlight.getSource()[1], brightness),
      );
      const b = Math.round(
        lerp(shadow.getSource()[2], hightlight.getSource()[2], brightness),
      );

      // Apply the duotone effect with opacity
      data[i] = lerp(data[i], r, this.opacity);
      data[i + 1] = lerp(data[i + 1], g, this.opacity);
      data[i + 2] = lerp(data[i + 2], b, this.opacity);
    }
  }

  /**
   * Send data from this filter to its shader program's uniforms.
   *
   * @param {WebGLRenderingContext} gl The GL canvas context used to compile this filter's shader.
   * @param {Object} uniformLocations A map of string uniform names to WebGLUniformLocation objects
   */
  sendUniformData(
    gl: WebGLRenderingContext,
    uniformLocations: TWebGLUniformLocationMap,
  ) {
    const highlight = Color.fromHex(this.highlight);
    const shadow = Color.fromHex(this.shadow);

    gl.uniform3fv(uniformLocations.uHighlight, [
      highlight.getSource()[0] / 255,
      highlight.getSource()[1] / 255,
      highlight.getSource()[2] / 255,
    ]);

    gl.uniform3fv(uniformLocations.uShadow, [
      shadow.getSource()[0] / 255,
      shadow.getSource()[1] / 255,
      shadow.getSource()[2] / 255,
    ]);

    gl.uniform1f(uniformLocations.uOpacity, this.opacity);
  }
}

function lerp(start: number, end: number, amount: number): number {
  return start * (1 - amount) + end * amount;
}

export const filters = {
  ...fabricFilters,
  Duotone,
};

export type Filters = Record<keyof typeof filters, any>;
